import { useCallback, useEffect, useState } from "react";
import { Box, Card, Grid, Typography, Button } from "@mui/material";
import * as Icons from "@mui/icons-material";
import { useAppDispatch } from "../../../store/store";
import { useSelector } from "react-redux";
import CallReviewAccordionItem from "./CallReviewAccordionItem";
import {
  getAuditDetails,
  getManualReview,
  updateManualReview,
} from "../../../services/audit.service";
import ErrorFallBack from "../../../layout/ErrorFallBack";
import {
  setAIEvaluation,
  setIsEditModeOn,
  setManualReview,
} from "../../../reduce-slice/AuditSlice";
import { useParams } from "react-router-dom";
import { groupBy } from "lodash";
import SkeletonLoaderCard from "../../../layout/loaders/SkeletonLoaderCard";
import CustomSnackbar, {
  useSnackbar,
} from "../../../components/ui/CustomSnackbar";

export const prepareManualDataResponse: any = (reviewList: any[]) => {
  const groupedCategories =
    groupBy(reviewList, "template_category.internal_id") ?? {};
  const updatedData = Object.values(groupedCategories).map((data) => ({
    ...data?.[0],
    questions: data.map((c: any) => ({
      template_category_question: c.template_category_question ?? {},
      category_question_evaluation_scores:
        c.category_question_evaluation_scores ?? {},
    })),
  }));
  return updatedData;
};

// Main Call Review Table component.
const CallReviewTable = () => {
  const dispatch = useAppDispatch();
  const { userId } = useParams();
  const {
    auditDetails,
    errorMsg,
    isEditModeOn,
    isAIReviewModeOn,
    manualReview,
    isAIEvaluation,
    auditDetailsLoader,
    updateManualReviewLoader,
  } = useSelector((state: any) => state.auditSlice);

  type ManualValuesType = {
    [key: string]: string; // or number, or whatever type your manual values are
  };
  const [manualValues, setManualValues] = useState<ManualValuesType>({});
  const handleToggle = () => {
    dispatch(setIsEditModeOn(!isEditModeOn));
  };

  const handleValueChange = (questionId: number, newValue: string) => {
    setManualValues((prevValues) => ({
      ...prevValues,
      [questionId]: newValue,
    }));
    setManualReview((prevValues: any) => ({
      ...prevValues,
      [questionId]: newValue,
    }));
  };

  const {
    snackbar: updateManualReviewSnackbar,
    showSnackbar: showManualReviewSnackbar,
    hideSnackbar: hideManualReviewSnackbar,
  } = useSnackbar();

  const handleCancel = () => {
    setManualValues({});
    setManualReview({});
    dispatch(setIsEditModeOn(false));
    dispatch(setAIEvaluation(false));
  };

  const addManualReview = () => {
    getUpdatedManualReview();
    dispatch(setIsEditModeOn(!isEditModeOn));
  };

  const getUpdatedManualReview = async () => {
    if (Array.isArray(auditDetails?.call_evaluation_manual_review)) {
      const updatedConstarAiDetailsList =
        auditDetails?.call_evaluation_manual_review.map((c: any) => ({
          ...c,
          value: manualValues[c.internal_id] || c.value,
        }));
      await dispatch(setManualReview(updatedConstarAiDetailsList));
      const response: any = await dispatch(
        updateManualReview(updatedConstarAiDetailsList)
      );
      if (response.payload?.message) {
        showManualReviewSnackbar(response.payload?.message, "success");
      }
      // console.log(manualReview, updatedConstarAiDetailsList, "manualReview");
      await dispatch(getAuditDetails(userId ?? ""));
    }
  };

  const getManualReviewData = useCallback(async () => {
    try {
      // Dispatch the API call with the constructed query string
      await dispatch(getManualReview(userId ?? ""))?.unwrap();
    } catch (error) {
      // console.error("Error fetching manual review:", error);
    }
  }, [userId, dispatch]);

  useEffect(() => {
    void getManualReviewData();
  }, [getManualReviewData]);

  return (
    <>
      <Card
        className="card-common discussion-card demo-portal-button main-card"
        sx={{ maxHeight: "850px", overflow: "auto" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          {!!auditDetails?.call_evaluation_manual_review?.length &&
            !isAIReviewModeOn && (
              <Box className="d-flex align-items-center justify-content-between mb-2">
                <Box>
                  {!isAIEvaluation &&
                    !(updateManualReviewLoader || auditDetailsLoader) && (
                      <Button
                        className="text-capitalize border-radius-40 me-0"
                        variant="contained"
                        color="primary"
                        size="medium"
                        startIcon={
                          isEditModeOn ? (
                            <Icons.SaveOutlined />
                          ) : (
                            <Icons.EditNote />
                          )
                        }
                        onClick={isEditModeOn ? addManualReview : handleToggle}
                      >
                        {isEditModeOn ? "Save" : "Add Manual Review"}
                      </Button>
                    )}

                  {isEditModeOn && (
                    <Button
                      variant="outlined"
                      size="medium"
                      onClick={handleCancel}
                      className="ms-2 cancel-btn"
                    >
                      Cancel
                    </Button>
                  )}
                </Box>
              </Box>
            )}
        </Box>
        {auditDetailsLoader || updateManualReviewLoader ? (
          <SkeletonLoaderCard heightNum={250} />
        ) : (
          <>
            <Box className="call-review-table-header">
              <Typography
                variant="subtitle1"
                fontSize={"12px"}
                width={"23%"}
                paddingLeft={"12px"}
              >
                Category
              </Typography>

              <Typography variant="subtitle1" fontSize={"12px"} width={"120px"}>
                Auditor Score
              </Typography>
              <Typography variant="subtitle1" fontSize={"12px"}>
                AI Score
              </Typography>
              <Typography
                variant="subtitle1"
                fontSize={"12px"}
                width={"23%"}
                paddingLeft={"12px"}
              >
                Auto Fail
              </Typography>
            </Box>

            <Grid item lg={12} md={12} sm={12}>
              <ErrorFallBack data={errorMsg}>
                <Box
                  className={`call-review-sub-table ${
                    !auditDetails?.call_evaluation_manual_review?.length
                      ? "d-flex align-items-center justify-content-center no-rec-sub-table"
                      : ""
                  }`}
                >
                  {!!auditDetails?.call_evaluation_manual_review?.length ? (
                    auditDetails.call_evaluation_manual_review?.map(
                      (row: any, idx: any) => (
                        <CallReviewAccordionItem
                          key={idx}
                          row={row}
                          isEditMode={isEditModeOn}
                          onValueChange={handleValueChange}
                          manualValues={manualValues}
                        />
                      )
                    )
                  ) : (
                    <Typography>Manual review data is unavailable.</Typography>
                  )}
                </Box>
              </ErrorFallBack>
            </Grid>
          </>
        )}
      </Card>
      <CustomSnackbar
        state={updateManualReviewSnackbar}
        onClose={hideManualReviewSnackbar}
      />
    </>
  );
};

export default CallReviewTable;
